import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ShieldCheck, LayoutGrid, Wrench, FileCheck2, Bell } from "lucide-react";
import { usePitch } from "../context/PitchContext";
import { Reveal, Eyebrow } from "./Reveal";
import { SampleTag } from "./SampleTag";

const S = {
  ok: { label: "In date", dot: "bg-emerald-500", pill: "bg-emerald-500/15 text-emerald-300", bar: "bg-emerald-500 w-[85%]" },
  soon: { label: "Due soon", dot: "bg-amber-400", pill: "bg-amber-400/15 text-amber-300", bar: "bg-amber-400 w-[25%]" },
  late: { label: "Expired", dot: "bg-red-500", pill: "bg-red-500/15 text-red-300", bar: "bg-red-500 w-[4%]" },
};

const certs = [
  ["Certificate of survey", "ok"],
  ["Certificate of operation", "ok"],
  ["Fire equipment service", "soon"],
  ["Liferaft service", "soon"],
  ["EPIRB registration", "late"],
  ["First aid kit check", "ok"],
];

const Sidebar = () => (
  <aside className="hidden w-48 shrink-0 border-r border-white/10 p-5 md:block">
    {[[LayoutGrid, "Overview"], [FileCheck2, "Certificates"], [Wrench, "Maintenance"], [Bell, "Alerts"]].map(([Icon, l], i) => (
      <p key={l} className={`mb-2 flex items-center gap-3 rounded-lg px-3 py-2 text-sm ${i === 1 ? "bg-white/10 text-white" : "text-slate-300"}`}>
        <Icon className="h-4 w-4" /> {l}
      </p>
    ))}
  </aside>
);

const Row = ({ name, s, i }) => (
  <motion.li
    initial={{ opacity: 0.001, x: -12 }}
    whileInView={{ opacity: 1, x: 0 }}
    viewport={{ once: true, amount: 0.1 }}
    transition={{ delay: 0.3 + i * 0.08, duration: 0.5 }}
    className="grid grid-cols-[1fr_auto] items-center gap-3 border-b border-white/5 py-3.5 md:grid-cols-[1fr_8rem_auto]"
  >
    <span className="flex items-center gap-3 text-sm text-slate-100 md:text-base">
      <span className={`h-2 w-2 shrink-0 rounded-full ${S[s].dot}`} />{name}
    </span>
    <span className="hidden h-1.5 rounded-full bg-white/10 md:block"><span className={`block h-1.5 rounded-full ${S[s].bar}`} /></span>
    <span className={`rounded-full px-3 py-1 text-xs font-medium ${S[s].pill}`}>{S[s].label}</span>
  </motion.li>
);

export const ProductScreen = () => {
  const pitch = usePitch();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "center center"] });
  const rotateX = useTransform(scrollYProgress, [0, 1], [22, 0]);
  const scale = useTransform(scrollYProgress, [0, 1], [0.9, 1]);

  return (
    <section id="screen" data-testid="product-screen-section" className="bg-[#0B192C] px-6 pb-24 pt-10 text-[#F4EDE3] md:px-10 md:pb-36">
      <div className="mx-auto max-w-6xl">
        <Reveal>
          <Eyebrow light>The product</Eyebrow>
          <h2 className="mt-4 font-display text-4xl leading-[1.05] tracking-tight md:text-6xl">
            Every certificate. <span className="italic text-[#D4A373]">One glance.</span>
          </h2>
        </Reveal>
        <div ref={ref} className="mt-12" style={{ perspective: 1400 }}>
          <motion.div
            data-testid="product-screen-mock"
            style={{ rotateX, scale, transformOrigin: "center top" }}
            className="overflow-hidden rounded-2xl border border-white/10 bg-[#0f2036] shadow-[0_40px_120px_-20px_rgba(200,125,85,0.35)]"
          >
            <div className="flex items-center gap-2 border-b border-white/10 px-5 py-3">
              <span className="h-2.5 w-2.5 rounded-full bg-white/20" /><span className="h-2.5 w-2.5 rounded-full bg-white/20" /><span className="h-2.5 w-2.5 rounded-full bg-white/20" />
              <span className="ml-3 font-mono text-[11px] tracking-[0.15em] text-slate-300">MARINEOS</span>
            </div>
            <div className="flex">
              <Sidebar />
              <div className="flex-1 p-5 md:p-8">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <p className="flex items-center gap-3 font-display text-2xl md:text-3xl"><ShieldCheck className="h-6 w-6 text-[#D4A373]" />{pitch.vesselName}</p>
                  <div className="flex gap-3 text-xs text-slate-300">
                    {Object.values(S).map((v) => <span key={v.label} className="flex items-center gap-1.5"><span className={`h-2 w-2 rounded-full ${v.dot}`} />{v.label}</span>)}
                  </div>
                </div>
                <ul className="mt-6">{certs.map(([n, s], i) => <Row key={n} name={n} s={s} i={i} />)}</ul>
              </div>
            </div>
          </motion.div>
        </div>
        <div className="mt-6"><SampleTag testId="product-screen-sample-label" /></div>
      </div>
    </section>
  );
};
